import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";
import Button from "../input/button";
import useSubscriptionAPIClient from "../../api/subscription/hooks/useSubscriptionAPIClient";
import useRevenueCat from "../../hooks/subscription/useRevenueCat";
import { Capacitor } from "@capacitor/core";

const Subscribed = () => {
  const { subscription } = useSelector((state) => state.user);
  const { getBillingPortalLink } = useSubscriptionAPIClient();
  const { openSubscriptionManagement } = useRevenueCat();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleManageSubscription = async () => {
    // Close the dialog
    setDialogOpen(false);

    // Use the subscription provider to determine which management interface to use
    // This is more accurate than checking platform, since a user could subscribe
    // on web (Stripe) and later open the mobile app
    if (subscription.provider === "google_play") {
      // Open Google Play subscription management
      openSubscriptionManagement();
    } else {
      // Open Stripe billing portal for web/Stripe subscribers
      setLoading(true);
      try {
        const { url } = await getBillingPortalLink();
        window.location.href = url;
      } catch (error) {
        console.error("Failed to get billing portal link:", error);
        // Fallback to Play Store if we're on native and Stripe fails
        if (Capacitor.isNativePlatform()) {
          openSubscriptionManagement();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const onManageClick = async () => {
    // Show dialog for Google Play subscribers or when on native platform
    if (
      subscription.provider === "google_play" ||
      Capacitor.isNativePlatform()
    ) {
      setDialogOpen(true);
    } else {
      // For Stripe subscribers on web, go directly to billing portal
      handleManageSubscription();
    }
  };

  const isPastDue = subscription.status === "past_due";
  const isCancelling =
    subscription.cancellingAt || subscription.status === "cancelling";

  let daysUntilCancellation = null;
  let cancellationLabel = "";
  if (subscription.cancellingAt) {
    const cancelDate = new Date(subscription.cancellingAt);
    daysUntilCancellation = Math.ceil(
      (cancelDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    );
    cancellationLabel =
      daysUntilCancellation > 0
        ? `in ${daysUntilCancellation} day${daysUntilCancellation === 1 ? "" : "s"}`
        : "today";
  }

  // Determine which subscription status label to show
  const getStatusLabel = () => {
    if (isPastDue) return "Payment Issue";
    if (isCancelling) return "Cancelling";
    return "Active";
  };

  const getStatusClassName = () => {
    if (isPastDue) return "account-page_subscription-type--past-due";
    if (isCancelling) return "account-page_subscription-type--cancelling";
    return "account-page_subscription-type--subscribed";
  };

  return (
    <>
      <div className="account-page_subscription-type-container">
        <Typography className="account-page_subscription-label">
          Subscription:
        </Typography>
        <Typography
          className={`account-page_subscription-type ${getStatusClassName()}`}
        >
          {getStatusLabel()}
        </Typography>
      </div>
      {isPastDue && (
        <Typography
          className="account-page_warning-label"
          sx={{ mb: { xs: 2 } }}
        >
          Your payment failed. Please update your payment method to avoid losing
          access.
        </Typography>
      )}
      {isCancelling && !isPastDue && subscription.cancellingAt && (
        <Typography
          className="account-page_cancel-label"
          sx={{ mb: { xs: 2 } }}
        >
          Your subscription will end {cancellationLabel}.
        </Typography>
      )}
      <Button
        text="Manage Subscription"
        size="huge"
        loading={loading}
        onClick={onManageClick}
      />

      {/* Subscription Management Warning Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Subscription</DialogTitle>
        <DialogContent>
          <Typography>
            Changes to your subscription status (such as cancelling or
            resubscribing) may take a short while to update in the app.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleManageSubscription}
            variant="contained"
            color="primary"
          >
            Continue
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Subscribed;
